"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Contact = void 0;
const StreamsTools_ts_1 = require("../toolkit/StreamsTools.js");
const patchedPryv_ts_1 = require("../patchedPryv.js");
const legacyChat_ts_1 = require("./legacyChat.js");
/**
 * Plan 66 composite-id base extractor. Refs serialise as either bare cuid
 * (`"abc123"`) for never-updated accesses or composite (`"abc123:3"`) for
 * updated heads / historical writes. Equality must be on the *base* — an
 * event's `modifiedBy` may carry a serial different from the current
 * access head, but it still attributes to the same access chain.
 *
 * Returns the input unchanged when `parseAccessRef` is unavailable
 * (older pryv lib), letting callers keep working under pre-Plan-66 servers.
 */
function refBase(ref) {
    if (ref == null)
        return null;
    const parse = patchedPryv_ts_1.pryv.utils?.parseAccessRef;
    if (typeof parse !== 'function')
        return ref;
    try {
        return parse(ref).base;
    }
    catch {
        return null;
    }
}
/**
 * Groups all CMC relationships from the same remote counterparty
 * (doctor, researcher, or bridge service).
 *
 * A Contact represents a person or service that has one or more CMC
 * data-grant accesses on the current user's account. Built via
 * `Contact.aggregateCmc(accesses, accepts, patientScope)` from the
 * patient's local counterparty accesses.
 */
class Contact {
    /** Remote user's Pryv username. null for bridge/service contacts. */
    remoteUsername;
    /** Display name (derived from counterparty username, can be overridden) */
    displayName;
    /** Raw Pryv counterparty access objects backing the relationships */
    accessObjects;
    /** Cached set of accessible stream IDs (built by initStreamCache) */
    #accessibleStreamIds;
    /**
     * Per access, the streams its `level: 'none'` entries forbid (with their model children), keyed by the same index as
     * `#grantsByAccess`. A mask only narrows the access it belongs to (B-2026-11-03-9).
     */
    #grantsByAccess = [];
    // ---- CMC-shaped fields (Plan 59 Phase 5a; canonical Plan 61 Phase C) ---- //
    /** CMC counterparty identity */
    counterparty;
    /** Person (doctor / researcher) or service (bridge) — derived from app-code prefix */
    kind;
    /** CMC relationship records — one per active data-grant on the patient side */
    cmcRelationships;
    constructor(remoteUsername, displayName) {
        this.remoteUsername = remoteUsername;
        this.displayName = displayName;
        this.accessObjects = [];
        this.#accessibleStreamIds = null;
        this.counterparty = null;
        this.kind = 'unknown';
        this.cmcRelationships = [];
    }
    /** Associate a raw access object with this contact */
    addAccessObject(access) {
        if (!this.accessObjects.find((a) => a.id === access.id)) {
            this.accessObjects.push(access);
        }
    }
    // ---- Stream cache & event filtering ---- //
    /**
     * Build the accessible stream IDs cache from all access permissions.
     * @param streamsById - map of streamId → stream object (with children) from the account
     */
    initStreamCache(streamsById) {
        this.#accessibleStreamIds = new Set();
        this.#grantsByAccess = [];
        for (const access of this.accessObjects) {
            if (access.deleted)
                continue;
            if (!access.permissions)
                continue;
            const grant = { ids: new Set(), forbidden: new Set() };
            this.#grantsByAccess.push(grant);
            for (const p of access.permissions) {
                if (!p.streamId)
                    continue;
                if (p.level === 'none') {
                    // An exclusion mask: the core refuses the stream and its children whatever else this access grants
                    // (B-2026-11-03-9; data-model 3.43.0 `explicitGrantOnly` roots get one on a minted `*` access).
                    grant.forbidden.add(p.streamId);
                    const masked = streamsById[p.streamId];
                    if (masked)
                        (0, StreamsTools_ts_1.getStreamIdAndChildrenIds)(masked).forEach((id) => grant.forbidden.add(id));
                    continue;
                }
                if (p.streamId === '*') {
                    if (!this.isPerson) {
                        // Bridge with wildcard: resolve bridge's own streams from access name
                        // (access.name is typically the bridge's base stream ID, e.g. "bridge-mira")
                        const bridgeStream = access.name ? streamsById[access.name] : null;
                        if (bridgeStream) {
                            const ids = (0, StreamsTools_ts_1.getStreamIdAndChildrenIds)(bridgeStream);
                            ids.forEach((id) => this.#accessibleStreamIds.add(id));
                        }
                        continue; // don't add wildcard for bridges
                    }
                    // wildcard covers everything for person contacts, but this access's masks still narrow it
                    this.#accessibleStreamIds.add('*');
                    grant.ids.add('*');
                    continue;
                }
                // The granted root is accessible whether or not the stream exists yet —
                // item streams are auto-created on first entry, AFTER this cache is
                // built. Skipping unknown roots made first-ever entries invisible
                // until the next contact rebuild (B-2026-07-02 patient-app flow).
                this.#accessibleStreamIds.add(p.streamId);
                grant.ids.add(p.streamId);
                const stream = streamsById[p.streamId];
                if (!stream)
                    continue;
                const ids = (0, StreamsTools_ts_1.getStreamIdAndChildrenIds)(stream);
                ids.forEach((id) => { this.#accessibleStreamIds.add(id); grant.ids.add(id); });
            }
        }
        // Plan 59 Phase 5a — also accept chat-stream ids from CMC relationships
        // (both the local outgoing stream on the patient's account and the
        // remote incoming stream on the doctor's account). Without this, doctor-
        // side chat events fetched via back-channel wouldn't pass
        // eventIsAccessible because they're not under any access permission
        // entry on the patient's side. Bare add — both streams have at most
        // one event each per chat message so no children to expand.
        const chat = { ids: new Set(), forbidden: new Set() };
        for (const rel of this.cmcRelationships) {
            if (rel.localChatStreamId) {
                this.#accessibleStreamIds.add(rel.localChatStreamId);
                chat.ids.add(rel.localChatStreamId);
            }
            if (rel.remoteChatStreamId) {
                this.#accessibleStreamIds.add(rel.remoteChatStreamId);
                chat.ids.add(rel.remoteChatStreamId);
            }
        }
        if (chat.ids.size > 0)
            this.#grantsByAccess.push(chat);
    }
    /**
     * Check if an event belongs to this contact's scope.
     * - Person contacts (doctors): event is in a stream covered by their access permissions
     * - Bridge/service contacts: event was created by the bridge OR is in the bridge's streams
     *   (bridges typically have wildcard `*` permissions but should only show their own data)
     */
    eventIsAccessible(event) {
        if (!this.isPerson) {
            // Bridge/service contacts: check authorship first (fastest)
            if (this.eventIsFromContact(event))
                return true;
            // Also check if event is in the bridge's own stream tree
            // (covers data created by older accesses not in previousAccessIds chain)
            if (this.#accessibleStreamIds && !this.#accessibleStreamIds.has('*') && event.streamIds) {
                for (const streamId of event.streamIds) {
                    if (this.#accessibleStreamIds.has(streamId))
                        return true;
                }
            }
            return false;
        }
        // Person contacts: filter by stream permissions, one access at a time (the platform's rule: an access reads an
        // event when one of its streams is granted and none of them is masked by that access)
        if (!this.#accessibleStreamIds)
            return false;
        const streamIds = event.streamIds ?? [];
        return this.#grantsByAccess.some((g) => {
            if (streamIds.some((id) => g.forbidden.has(id)))
                return false;
            return g.ids.has('*') || streamIds.some((id) => g.ids.has(id));
        });
    }
    /** Check if an event was created/modified by this contact (including replaced accesses) */
    eventIsFromContact(event) {
        const eventBase = refBase(event.modifiedBy);
        if (eventBase == null)
            return false;
        for (const access of this.accessObjects) {
            // Plan 66: access.id may be composite (`<base>:<serial>`) on updated accesses.
            // An event's modifiedBy carries the serial active at write time, possibly
            // different from the current head. Compare on base only.
            if (refBase(access.id) === eventBase)
                return true;
            // Check previous access IDs from replaced accesses (collector pattern, legacy delete+create).
            // The historical chain stays in clientData even after Plan 58 switches to in-place update.
            const collectorPrevIds = access.clientData?.hdsCollectorClient?.previousAccessIds;
            if (Array.isArray(collectorPrevIds)) {
                for (const prev of collectorPrevIds) {
                    if (refBase(prev) === eventBase)
                        return true;
                }
            }
            // Check previous access IDs from bridge access recreate pattern (legacy)
            const bridgePrevIds = access.clientData?.previousAccessIds;
            if (Array.isArray(bridgePrevIds)) {
                for (const prev of bridgePrevIds) {
                    if (refBase(prev) === eventBase)
                        return true;
                }
            }
        }
        return false;
    }
    /** Determine chat event source: 'me', 'contact', or 'unknown' */
    chatEventInfos(event) {
        // Each relationship has ONE local conversation stream holding BOTH
        // directions. The CMC plugin stamps `content.from = {username,...}` on
        // delivered (incoming) events; the local outgoing trigger has no `from`.
        // So within the local chat stream, classify by `content.from`. (Legacy:
        // events still arriving on a counterparty's remoteChatStreamId — pre-mirror
        // back-channel reads — are always the counterparty's.)
        if (!event.streamIds)
            return { source: 'unknown' };
        for (const rel of this.cmcRelationships) {
            if (!rel.features.chat)
                continue;
            if (rel.localChatStreamId && event.streamIds.includes(rel.localChatStreamId)) {
                const from = event.content?.from;
                return { source: from?.username ? 'contact' : 'me' };
            }
            if (rel.remoteChatStreamId && event.streamIds.includes(rel.remoteChatStreamId)) {
                return { source: 'contact' };
            }
        }
        return { source: 'unknown' };
    }
    // ---- Pre-CMC chat, read-only (legacyChat.ts) ---- //
    /** This contact's pre-CMC chat stream ids (`chat-<remoteUsername>`, `chat-<remoteUsername>-in`), null without account name. */
    get legacyChatStreams() {
        return (0, legacyChat_ts_1.legacyChatStreams)(this.remoteUsername);
    }
    /** Who wrote a pre-CMC chat event of this contact ('me', 'contact'), 'unknown' when it is not one. `chatEventInfos` is unchanged. */
    legacyChatSource(event) {
        return (0, legacyChat_ts_1.legacyChatSource)(event, this.remoteUsername);
    }
    /** This contact's pre-CMC messages among `events`, oldest first, read-only. */
    legacyChatMessages(events) {
        return (0, legacyChat_ts_1.legacyChatMessages)(this.remoteUsername, events);
    }
    // ---- Derived getters (CMC-only) ---- //
    /** Overall status — derived from cmcRelationships. */
    get status() {
        if (this.cmcRelationships.some(r => r.acceptedAt != null))
            return 'Active';
        if (this.cmcRelationships.length > 0)
            return 'Incoming';
        return null;
    }
    /** Any chat-enabled relationship → true. */
    get hasChat() {
        return this.cmcRelationships.some(r => r.features.chat);
    }
    /** Distinct CMC app codes attached to this contact. */
    get appStreamIds() {
        const ids = [];
        for (const rel of this.cmcRelationships) {
            if (rel.appCode && !ids.includes(rel.appCode))
                ids.push(rel.appCode);
        }
        return ids;
    }
    /** Aggregated granted permissions across CMC relationships, deduped. */
    get allPermissions() {
        return this.cmcAllPermissions;
    }
    /** Any relationship has been accepted. */
    get isActive() {
        return this.cmcRelationships.some(r => r.acceptedAt != null);
    }
    /** Person (has remote username) vs service. */
    get isPerson() {
        return this.remoteUsername !== null;
    }
    /** Patient-side counterparty access ids backing this contact. */
    get accessIds() {
        return this.accessObjects.map(a => a?.id).filter((id) => typeof id === 'string');
    }
    // ---- Plan 59 Phase 5a — CMC getters ---- //
    /** Active CMC relationships exist (at least one counterparty access) */
    get cmcIsActive() {
        return this.cmcRelationships.length > 0;
    }
    /** Any relationship has chat negotiated */
    get cmcHasChat() {
        return this.cmcRelationships.some(r => r.features.chat);
    }
    /**
     * Chat-stream descriptors, one per chat-enabled relationship.
     *
     * `read` is the doctor-side stream id (read via `counterpartyApiEndpoint`
     * — caller must construct a `pryv.Connection(counterpartyApiEndpoint)`).
     * `write` is the patient-side stream id on the patient's own connection.
     */
    get cmcChatStreams() {
        return this.cmcRelationships
            .filter(r => r.features.chat)
            .map(r => ({
            read: r.remoteChatStreamId,
            write: r.localChatStreamId,
            counterpartyApiEndpoint: r.counterpartyApiEndpoint,
            accessId: r.accessId
        }));
    }
    /**
     * Resolved FormSpec snapshots across all active CMC relationships.
     * Skips relationships whose accept event hasn't been mirrored yet
     * (hdsFormSpec === null). One entry per relationship that has a spec.
     */
    get cmcFormSpecs() {
        const result = [];
        for (const rel of this.cmcRelationships) {
            if (rel.hdsFormSpec)
                result.push(rel.hdsFormSpec);
        }
        return result;
    }
    /**
     * Aggregated form sections across all active CMC relationships (Tasks
     * page recurring-task loop reads this). Pulled from each relationship's
     * resolved FormSpec; empty if no FormSpecs are mirrored yet.
     */
    get cmcFormSections() {
        const sections = [];
        for (const rel of this.cmcRelationships) {
            const spec = rel.hdsFormSpec;
            if (!spec || !Array.isArray(spec.sections))
                continue;
            // FormSpec sections are AppTemplateSection — cast to the legacy
            // CollectorSectionInterface for consumer compatibility (same shape
            // up to a couple of optional fields).
            for (const s of spec.sections) {
                sections.push(s);
            }
        }
        return sections;
    }
    /** Aggregated granted permissions across all active CMC relationships (deduped by streamId:level) */
    get cmcAllPermissions() {
        const seen = new Set();
        const result = [];
        for (const rel of this.cmcRelationships) {
            for (const p of rel.grantedPermissions) {
                const key = `${p.streamId}:${p.level}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    result.push(p);
                }
            }
        }
        return result;
    }
    // ---- Plan 59 Phase 5a — CMC static helpers ---- //
    /**
     * Person vs service detection from CMC app-code (Q-C1 resolution).
     * `hds-bridge-*` → service; anything else non-null → person; null → unknown.
     */
    static cmcDetectKind(appCode) {
        if (!appCode)
            return 'unknown';
        if (appCode.startsWith('hds-bridge-'))
            return 'service';
        return 'person';
    }
    /**
     * Build Contacts from the patient's local counterparty accesses.
     *
     * The counterparty access (`clientData.cmc.role === 'counterparty'`) is
     * the source of truth: it carries the counterparty's apiEndpoint +
     * remote stream ids in `clientData.cmc.counterparty.*`. Accept events
     * (from `cmc.listAcceptedRelationships`) are used only to enrich
     * relationships with `acceptedAt` + `acceptEventId`.
     *
     * Per Q-C2 (CMC plugin Q-C2 resolution 2026-05-21): stale accept events
     * whose backing counterparty access has been revoked are simply dropped —
     * no access → no relationship in the output.
     *
     * @param accesses Patient's accesses from `connection.api('accesses.get')`.
     * @param accepts  Patient's accepted relationships from `cmc.listAcceptedRelationships`.
     * @param patientScopeStreamId The patient's hds-webapp scope (e.g. `:_cmc:apps:hds-patient`).
     *                              Used to build the local chat-stream id.
     */
    static aggregateCmc(accesses, accepts, patientScopeStreamId) {
        const byCounterparty = new Map();
        for (const access of accesses) {
            if (access.deleted)
                continue;
            const cmcData = access.clientData?.cmc;
            if (cmcData?.role !== 'counterparty')
                continue;
            const cp = cmcData.counterparty;
            if (!cp?.username || !cp?.host)
                continue;
            const key = `${cp.username.toLowerCase()}@${cp.host.toLowerCase()}`;
            let contact = byCounterparty.get(key);
            if (!contact) {
                contact = new Contact(cp.username, cp.username);
                contact.counterparty = { username: cp.username, host: cp.host };
                contact.kind = Contact.cmcDetectKind(cmcData.appCode);
                byCounterparty.set(key, contact);
            }
            const appCode = cmcData.appCode || 'unknown';
            // peerSlug for the LOCAL chat stream — slug of the counterparty.
            // Use cmc.counterpartySlug to stay aligned with the SDK / plugin
            // (which canonicalize host casing + strip ports).
            let peerSlug;
            try {
                peerSlug = patchedPryv_ts_1.cmc.counterpartySlug({ username: cp.username, host: cp.host });
            }
            catch {
                // Fallback if the SDK helper is missing in some environment — shouldn't happen
                // in practice, but keeps the aggregator side-effect-free under tests.
                peerSlug = `${cp.username.toLowerCase()}--${cp.host.toLowerCase().replace(/:\d+$/, '').replace(/\./g, '-')}`;
            }
            // Local outgoing chat stream. The CMC plugin provisions the relationship's
            // chat streams under the INVITING app's scope (e.g. the collector's
            // `:_cmc:apps:hds-collector:<collectorId>`) and mirrors that scope onto the
            // accepter's account — NOT under a generic patient app scope. On accept it
            // grants this access `contribute` on the accepter's own per-counterparty
            // chat stream. Prefer that server-granted stream (authoritative: it exists
            // and is writable); else derive from the remote chat stream's scope; else
            // fall back to the legacy patientScopeStreamId. (`clientData.cmc.appCode`
            // is only stamped on counterparty accesses by cores from 2026-07 on, and is
            // null on older ones, so the scope can't come from appCode.)
            const grantedChat = (access.permissions ?? []).find(p => typeof p.streamId === 'string' &&
                p.streamId.endsWith(`:chats:${peerSlug}`) &&
                (p.level === 'contribute' || p.level === 'manage'));
            let localChatStreamId;
            if (grantedChat) {
                localChatStreamId = grantedChat.streamId;
            }
            else if (cp.remoteChatStreamId) {
                const remoteScope = cp.remoteChatStreamId.replace(/:chats:[^:]+$/, '');
                localChatStreamId = `${remoteScope}:chats:${peerSlug}`;
            }
            else {
                localChatStreamId = `${patientScopeStreamId}:chats:${peerSlug}`;
            }
            // Same derivation for the system channel (collectors anchor), used for
            // data-export requests and their acks. No legacy fallback: the anchor only
            // exists where the plugin provisioned it.
            const grantedCollector = (access.permissions ?? []).find(p => typeof p.streamId === 'string' &&
                p.streamId.endsWith(`:collectors:${peerSlug}`) &&
                (p.level === 'contribute' || p.level === 'manage'));
            const localCollectorStreamId = grantedCollector
                ? grantedCollector.streamId
                : (cp.remoteCollectorStreamId
                    ? `${cp.remoteCollectorStreamId.replace(/:collectors:[^:]+$/, '')}:collectors:${peerSlug}`
                    : null);
            // Match THIS access's own accept event: the same counterparty can hold
            // several data sets (one access + one accept each), and each carries its
            // own FormSpec and acceptedAt (B-2026-10-14-1). The core stamps the accept
            // id on the data grant (`clientData.cmc.acceptEventId`) and the accept
            // names its grant (`dataGrantAccessId`). Only an accept that names no
            // grant (older events) falls back to (counterparty, appCode), so it can
            // never borrow another data set's accept.
            const ownAcceptEventId = cmcData.acceptEventId;
            const matchingAccept = (ownAcceptEventId ? accepts.find(a => a.acceptEventId === ownAcceptEventId) : undefined) ??
                accepts.find(a => a.dataGrantAccessId != null && refBase(a.dataGrantAccessId) === refBase(access.id)) ??
                accepts.find(a => a.dataGrantAccessId == null &&
                    a.counterparty?.username?.toLowerCase() === cp.username.toLowerCase() &&
                    a.counterparty?.host?.toLowerCase() === cp.host.toLowerCase() &&
                    (a.appCode || 'unknown') === appCode);
            const f = cmcData.features ?? null;
            const features = {
                chat: !!f?.chat,
                systemMessaging: !!(f?.systemMessaging ?? f?.system)
            };
            const rel = {
                accessId: access.id,
                acceptEventId: matchingAccept?.acceptEventId ?? null,
                counterparty: { username: cp.username, host: cp.host },
                counterpartyApiEndpoint: cp.apiEndpoint ?? null,
                remoteChatStreamId: cp.remoteChatStreamId ?? null,
                remoteCollectorStreamId: cp.remoteCollectorStreamId ?? null,
                localChatStreamId,
                localCollectorStreamId,
                appCode,
                features,
                grantedPermissions: access.permissions ?? [],
                acceptedAt: matchingAccept?.acceptedAt ?? null,
                hdsFormSpec: matchingAccept?.hdsFormSpec ?? null
            };
            contact.cmcRelationships.push(rel);
            contact.addAccessObject(access);
        }
        return Array.from(byCounterparty.values());
    }
}
exports.Contact = Contact;
